import React, { useState, useEffect, useRef, useCallback } from "react";
import { getSetting, addOrUpdateSetting } from "../../api/settingApi";
import Loader from "../common/Loader";
import ToggleSwitch from "../common/ToggleSwitch";
import PermissionGate from "../common/PermissionGate";
import { PERMISSIONS } from "../../api/permissions";
import {
  parseDateTime,
  CalendarDateTime,
  now,
  getLocalTimeZone,
} from "@internationalized/date";
import { DatePicker } from "../common/DatePicker";

// ---- Convert backend string / native Date → CalendarDateTime ----
const toCalendarDateTime = (value) => {
  if (!value) return null;
  if (value instanceof CalendarDateTime) return value;
  if (value instanceof Date) {
    return new CalendarDateTime(
      value.getFullYear(),
      value.getMonth() + 1,
      value.getDate(),
      value.getHours(),
      value.getMinutes(),
      value.getSeconds(),
    );
  }
  try {
    return parseDateTime(String(value));
  } catch {
    return null;
  }
};

// ---- Convert CalendarDateTime / native Date → backend string ----
const formatLocalDateTime = (date) => {
  if (!date) return null;
  const pad = (n) => String(n).padStart(2, "0");

  if (date instanceof CalendarDateTime) {
    return `${date.year}-${pad(date.month)}-${pad(date.day)}T${pad(
      date.hour,
    )}:${pad(date.minute)}:${pad(date.second ?? 0)}`;
  }

  if (date instanceof Date) {
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
      date.getDate(),
    )}T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(
      date.getSeconds(),
    )}`;
  }

  return null;
};

const SATURDAY = 6;
const SUNDAY = 0;

const toWeekendDaysArray = ({ saturday, sunday }) => {
  const result = [];
  if (saturday) result.push(SATURDAY);
  if (sunday) result.push(SUNDAY);
  return result;
};

const fromWeekendDaysArray = (arr) => {
  const days = Array.isArray(arr) ? arr : [];
  return {
    saturday: days.includes(SATURDAY),
    sunday: days.includes(SUNDAY),
  };
};

const BusinessTerminalSettings = ({ businessId, showToast }) => {
  const [terminals, setTerminals] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [currentVersion, setCurrentVersion] = useState(0);
  const [showLogModal, setShowLogModal] = useState(false);

  const [commonSettings, setCommonSettings] = useState({
    refundEnabled: true,
    holidaySurchargeEnabled: false,
  });

  const [weekendDays, setWeekendDays] = useState({
    saturday: false,
    sunday: false,
  });

  const [weekendSrchP, setWeekendSrchP] = useState("");

  const [phSurcharge, setPhSurcharge] = useState({
    id: 0,
    desc: "Public Holiday Surcharge",
    from: null,
    to: null,
    srchPercentage: "",
  });
  const [errors, setErrors] = useState({});
  const [isNewPhEntry, setIsNewPhEntry] = useState(true);

  const fetchCalledRef = useRef(false);
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const atLeastOneWeekendDay = weekendDays.saturday || weekendDays.sunday;
  const weekendSrchEnabled = atLeastOneWeekendDay;

  const populateFromTerminalData = useCallback((terminalList) => {
    if (terminalList.length > 0 && terminalList[0]) {
      const firstTerminal = terminalList[0];
      const phSurcharges = firstTerminal.phSurcharges || [];

      const restoredDays = fromWeekendDaysArray(firstTerminal.weekendSrchDays);
      setWeekendDays(restoredDays);

      setWeekendSrchP(
        firstTerminal.weekendSrchP !== undefined &&
          firstTerminal.weekendSrchP !== null
          ? String(firstTerminal.weekendSrchP)
          : "",
      );

      setCommonSettings({
        refundEnabled: firstTerminal.refundEnabled ?? true,
        holidaySurchargeEnabled: phSurcharges.length > 0,
      });

      if (phSurcharges.length > 0) {
        const ph = phSurcharges[0];
        setPhSurcharge({
          id: ph.id ?? 0,
          desc: ph.desc || "Public Holiday Surcharge",
          from: toCalendarDateTime(ph.from),
          to: toCalendarDateTime(ph.to),
          srchPercentage:
            ph.srchPercentage !== undefined && ph.srchPercentage !== null
              ? String(ph.srchPercentage)
              : "",
        });
        setIsNewPhEntry(false);
      } else {
        setPhSurcharge({
          id: 0,
          desc: "Public Holiday Surcharge",
          from: null,
          to: null,
          srchPercentage: "",
        });
        setIsNewPhEntry(true);
      }
    }
  }, []);

  const fetchTerminalsetting = useCallback(async () => {
    if (!businessId) return;
    if (fetchCalledRef.current) return;
    fetchCalledRef.current = true;

    setLoading(true);
    try {
      const response = await getSetting(businessId);
      const terminalList = response.data || [];
      const version = response.version || 0;

      if (isMountedRef.current) {
        setCurrentVersion(version);
        setTerminals(terminalList);
        populateFromTerminalData(terminalList);
      }
    } catch (err) {
      if (isMountedRef.current) {
        showToast("Failed to load settings", "error");
      }
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
        setTimeout(() => {
          fetchCalledRef.current = false;
        }, 300);
      }
    }
  }, [businessId, showToast, populateFromTerminalData]);

  useEffect(() => {
    fetchTerminalsetting();
  }, [fetchTerminalsetting]);

  const handleEditClick = () => {
    setIsEditing(true);
    setErrors({});
  };

  const handleCancelEdit = () => {
    populateFromTerminalData(terminals);
    setErrors({});
    setIsEditing(false);
  };

  const computeErrors = useCallback(
    (state) => {
      const newErrors = {};

      if (atLeastOneWeekendDay) {
        if (!weekendSrchP && weekendSrchP !== 0) {
          newErrors.weekendSrchP = "Surcharge percentage is required";
        } else {
          const val = parseFloat(weekendSrchP);
          if (isNaN(val) || val < 0 || val > 25) {
            newErrors.weekendSrchP = "Percentage must be between 0 and 25";
          }
        }
      }

      if (state && state.holidayEnabled) {
        if (!state.from) {
          newErrors.from = "From date is required";
        }

        if (!state.to) {
          newErrors.to = "To date is required";
        }

        if (state.from && state.to && state.to.compare(state.from) < 0) {
          newErrors.to = "To date cannot be earlier than From date";
        }

        if (!state.srchPercentage) {
          newErrors.srchPercentage = "Surcharge percentage is required";
        } else {
          const val = parseFloat(state.srchPercentage);
          if (isNaN(val) || val < 0 || val > 25) {
            newErrors.srchPercentage = "Percentage must be between 0 and 25";
          }
        }
      }

      return newErrors;
    },
    [atLeastOneWeekendDay, weekendSrchP],
  );

  const validateAll = () => {
    const newErrors = computeErrors({
      holidayEnabled: commonSettings.holidaySurchargeEnabled,
      from: phSurcharge.from,
      to: phSurcharge.to,
      srchPercentage: phSurcharge.srchPercentage,
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleFromChange = (date) => {
    const updated = { ...phSurcharge, from: date };
    setPhSurcharge(updated);
    setErrors(
      computeErrors({
        holidayEnabled: commonSettings.holidaySurchargeEnabled,
        from: updated.from,
        to: updated.to,
        srchPercentage: updated.srchPercentage,
      }),
    );
  };

  const handleToChange = (date) => {
    const updated = { ...phSurcharge, to: date };
    setPhSurcharge(updated);
    setErrors(
      computeErrors({
        holidayEnabled: commonSettings.holidaySurchargeEnabled,
        from: updated.from,
        to: updated.to,
        srchPercentage: updated.srchPercentage,
      }),
    );
  };

  const handleSrchPercentageChange = (value) => {
    const updated = { ...phSurcharge, srchPercentage: value };
    setPhSurcharge(updated);
    setErrors(
      computeErrors({
        holidayEnabled: commonSettings.holidaySurchargeEnabled,
        from: updated.from,
        to: updated.to,
        srchPercentage: updated.srchPercentage,
      }),
    );
  };

  const handleWeekendSrchPChange = (value) => {
    setWeekendSrchP(value);
    setErrors((prev) => {
      const next = { ...prev };
      delete next.weekendSrchP;
      if (atLeastOneWeekendDay) {
        if (!value && value !== 0) {
          next.weekendSrchP = "Surcharge percentage is required";
        } else {
          const val = parseFloat(value);
          if (isNaN(val) || val < 0 || val > 25) {
            next.weekendSrchP = "Percentage must be between 0 and 25";
          }
        }
      }
      return next;
    });
  };

  const handleSaturdayChange = (checked) => {
    const updated = { ...weekendDays, saturday: checked };
    setWeekendDays(updated);
    if (!updated.saturday && !updated.sunday) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next.weekendSrchP;
        return next;
      });
    }
  };

  const handleSundayChange = (checked) => {
    const updated = { ...weekendDays, sunday: checked };
    setWeekendDays(updated);
    if (!updated.saturday && !updated.sunday) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next.weekendSrchP;
        return next;
      });
    }
  };

  const handleSaveCommonSettings = async () => {
    if (!validateAll()) return;

    if (terminals.length === 0) {
      showToast("No terminals available to update", "warning");
      return;
    }

    setIsSaving(true);
    try {
      let phSurchargesData = [];
      if (commonSettings.holidaySurchargeEnabled) {
        const existingId =
          !isNewPhEntry && phSurcharge.id != null ? phSurcharge.id : 0;

        phSurchargesData = [
          {
            id: existingId,
            desc: (phSurcharge.desc || "Public Holiday Surcharge").trim(),
            from: formatLocalDateTime(phSurcharge.from),
            to: formatLocalDateTime(phSurcharge.to),
            srchPercentage: parseFloat(phSurcharge.srchPercentage) || 0,
          },
        ];
      }

      const terminalData = {
        weekendSrchEnabled: weekendSrchEnabled,
        weekendSrchP: atLeastOneWeekendDay ? parseFloat(weekendSrchP) || 0 : 0,
        refundEnabled: commonSettings.refundEnabled,
        weekendSrchDays: toWeekendDaysArray(weekendDays),
        phSurcharges: phSurchargesData,
      };

      const response = await addOrUpdateSetting(
        businessId,
        terminalData,
        currentVersion,
      );

      const updatedBusinessData = response?.data ?? response;
      const newVersion = response?.version ?? currentVersion;

      setCurrentVersion(newVersion);

      if (updatedBusinessData) {
        const updatedTerminals = Array.isArray(updatedBusinessData)
          ? updatedBusinessData
          : [updatedBusinessData];
        setTerminals(updatedTerminals);
        populateFromTerminalData(updatedTerminals);
      }

      setIsEditing(false);
      setErrors({});
      showToast("Properties updated successfully", "success");
    } catch (err) {
      if (err.statusCode === 409) {
        const conflictMessage =
          err.details || "Conflict detected. Please refresh and try again.";
        showToast(conflictMessage, "warning", true);

        fetchCalledRef.current = false;
        try {
          await fetchTerminalsetting();
        } finally {
          setIsEditing(false);
          setErrors({});
        }
      } else {
        const errMsg =
          err.response?.data?.message ||
          err.message ||
          "Failed to update properties";
        showToast(errMsg, "error");
      }
    } finally {
      setIsSaving(false);
    }
  };

  const isPhDisabled =
    loading || !isEditing || !commonSettings.holidaySurchargeEnabled;

  const todayMin = now(getLocalTimeZone());

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h6 className="mb-0">
          <i className="bi bi-sliders me-2"></i>
          Properties
        </h6>
      </div>

      {loading && terminals.length === 0 ? (
        <Loader message="Loading settings..." size="medium" />
      ) : (
        <>
          <h6>Weekend Surcharge</h6>
          <div className="row align-items-end">
            <div className="col-md-2 mb-3">
              <label className="form-label d-block">Saturday</label>
              <ToggleSwitch
                id="saturdayToggle"
                checked={weekendDays.saturday}
                onChange={handleSaturdayChange}
                disabled={loading || !isEditing || isSaving}
              />
            </div>
            <div className="col-md-2 mb-3">
              <label className="form-label d-block">Sunday</label>
              <ToggleSwitch
                id="sundayToggle"
                checked={weekendDays.sunday}
                onChange={handleSundayChange}
                disabled={loading || !isEditing || isSaving}
              />
            </div>
            <div className="col-md-3 mb-3">
              <label className="form-label">
                Surcharge(%) {atLeastOneWeekendDay && "*"}
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                max="25"
                className={`form-control ${
                  errors.weekendSrchP ? "is-invalid" : ""
                }`}
                value={weekendSrchP}
                onChange={(e) => handleWeekendSrchPChange(e.target.value)}
                placeholder="0"
                disabled={
                  loading || !isEditing || !atLeastOneWeekendDay || isSaving
                }
              />
              {errors.weekendSrchP && (
                <div className="invalid-feedback d-block">
                  {errors.weekendSrchP}
                </div>
              )}
            </div>
            <div className="col-md-3 mb-3">
              <label className="form-label d-block">Refund Enabled</label>
              <ToggleSwitch
                id="refundToggle"
                checked={commonSettings.refundEnabled}
                onChange={(checked) =>
                  setCommonSettings((prev) => ({
                    ...prev,
                    refundEnabled: checked,
                  }))
                }
                disabled={loading || !isEditing || isSaving}
              />
            </div>
          </div>

          <hr />

          <div className="row mb-3">
            <div className="col-md-12 mb-3">
              <label className="form-label d-block">Holiday Surcharge</label>
              <ToggleSwitch
                id="holidayToggle"
                checked={commonSettings.holidaySurchargeEnabled}
                onChange={(checked) => {
                  setCommonSettings((prev) => ({
                    ...prev,
                    holidaySurchargeEnabled: checked,
                  }));
                  if (!checked) {
                    setPhSurcharge({
                      id: 0,
                      desc: "Public Holiday Surcharge",
                      from: null,
                      to: null,
                      srchPercentage: "",
                    });
                    setErrors((prev) => {
                      const next = { ...prev };
                      delete next.from;
                      delete next.to;
                      delete next.srchPercentage;
                      return next;
                    });
                  } else if (isNewPhEntry) {
                    setPhSurcharge((prev) => ({
                      ...prev,
                      desc: "Public Holiday Surcharge",
                    }));
                  }
                }}
                disabled={loading || !isEditing || isSaving}
              />
            </div>
          </div>

          {commonSettings.holidaySurchargeEnabled && (
            <div className="row g-3 align-items-end mb-3 p-3 border-peach-glow rounded">
              <div className="col-md-3">
                <label className="form-label d-block">DESC</label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="e.g., Christmas"
                  value={phSurcharge.desc}
                  disabled
                />
              </div>

              <div className="col-md-3">
                <label className="form-label d-block">From *</label>
                <div className="datepicker-wrapper">
                  <DatePicker
                    value={phSurcharge.from}
                    onChange={handleFromChange}
                    showTimeSelect
                    granularity="minute"
                    hourCycle={24}
                    isInvalid={!!errors.from}
                    isDisabled={isPhDisabled || isSaving}
                    //minValue={todayMin}
                    popperPlacement="bottom-start"
                  />
                </div>
                {errors.from && (
                  <div className="invalid-feedback d-block">{errors.from}</div>
                )}
              </div>

              <div className="col-md-3">
                <label className="form-label d-block">To *</label>
                <div className="datepicker-wrapper">
                  <DatePicker
                    value={phSurcharge.to}
                    onChange={handleToChange}
                    showTimeSelect
                    granularity="minute"
                    hourCycle={24}
                    isInvalid={!!errors.to}
                    isDisabled={isPhDisabled || isSaving}
                    minValue={phSurcharge.from}
                    popperPlacement="bottom-start"
                  />
                </div>
                {errors.to && (
                  <div className="invalid-feedback d-block">{errors.to}</div>
                )}
              </div>

              <div className="col-md-3">
                <label className="form-label d-block">Surcharge(%) *</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  max="25"
                  className={`form-control ${
                    errors.srchPercentage ? "is-invalid" : ""
                  }`}
                  placeholder="0.00"
                  value={phSurcharge.srchPercentage}
                  onChange={(e) => handleSrchPercentageChange(e.target.value)}
                  disabled={isPhDisabled || isSaving}
                />
                {errors.srchPercentage && (
                  <div className="invalid-feedback d-block">
                    {errors.srchPercentage}
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="text-end mb-3">
            {!isEditing ? (
              <PermissionGate permission={PERMISSIONS.BUSINESS_UPDATE_PROPS}>
                <button
                  className="btn btn-primary btn-sm"
                  onClick={handleEditClick}
                  disabled={loading || terminals.length === 0 || isSaving}
                >
                  <i className="bi bi-pencil me-1"></i>
                  Edit Settings
                </button>
              </PermissionGate>
            ) : (
              <>
                <button
                  className="btn btn-secondary btn-sm me-2"
                  onClick={handleCancelEdit}
                  disabled={isSaving}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-primary btn-sm"
                  onClick={handleSaveCommonSettings}
                  disabled={isSaving}
                >
                  {isSaving ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2"></span>
                      Saving...
                    </>
                  ) : (
                    <>Save Changes</>
                  )}
                </button>
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default BusinessTerminalSettings;
